/*
 * "Grow over time": the daily visits rise by a percentage every day, week or month, up to a cap.
 *
 * The campaign keeps its base in settings.scheduleTrafficAmount. What is delivered today lives in
 * settings.growth.current and in the project's daily_limit; a nightly job (backend/scripts/
 * growth_daily.py) moves both forward for running campaigns and counts only the days a campaign ran,
 * so a pause holds the growth instead of skipping ahead.
 */
import type { ProjectSettings } from '../types';

export type GrowthEvery = 'day' | 'week' | 'month';

export interface GrowthSettings {
    enabled: boolean;
    percent: number;
    every: GrowthEvery;
    cap: number;
    /** Daily visits the growth started from; set when it is switched on or restarted. */
    base?: number;
    /** ISO date of the start or last restart. */
    startedAt?: string;
    /** Days the campaign actually ran since startedAt (maintained by the nightly job). */
    activeDays?: number;
    /** Daily visits delivered today. */
    current?: number;
}

export const GROWTH_EVERY_DAYS: Record<GrowthEvery, number> = { day: 1, week: 7, month: 30 };
export const GROWTH_PERCENTS = [3, 5, 10, 15, 20, 25, 50];

/**
 * Shown in the dev server; in a production build only once the nightly job runs (VITE_GROWTH_OVER_TIME=1).
 * Plain member access on purpose: Vite only replaces `import.meta.env.X`, not `import.meta.env?.X`.
 */
export const GROWTH_UI_ENABLED = import.meta.env.DEV || import.meta.env.VITE_GROWTH_OVER_TIME === '1';

export const defaultGrowth = (base: number): GrowthSettings => ({
    enabled: true,
    percent: 10,
    every: 'week',
    cap: Math.max(base * 2, base + 100),
});

/** Daily visits after `day` running days, starting from `base`. */
export const growthValueAt = (base: number, growth: Pick<GrowthSettings, 'percent' | 'every' | 'cap'>, day: number): number => {
    const steps = Math.floor(Math.max(0, day) / GROWTH_EVERY_DAYS[growth.every]);
    const grown = Math.round(base * Math.pow(1 + growth.percent / 100, steps));
    return Math.min(grown, Math.max(growth.cap, base));
};

/** Daily visits at the start of the next `count` periods, beginning with today. */
export const growthPlan = (base: number, growth: GrowthSettings, count = 8): number[] =>
    Array.from({ length: count }, (_, i) => growthValueAt(base, growth, i * GROWTH_EVERY_DAYS[growth.every]));

/** First period index (0-based) at which the cap is reached, or -1. */
export const growthCapPeriod = (plan: number[], cap: number): number => plan.findIndex((value) => value >= cap);

/** Credits the next `days` days use, starting tomorrow from where the growth stands today. */
export const growthCredits = (base: number, growth: GrowthSettings, fromDay: number, days = 30): number => {
    let sum = 0;
    for (let d = 1; d <= days; d += 1) sum += growthValueAt(base, growth, fromDay + d);
    return sum;
};

/** Days until `balance` credits run out at the growing pace, or null if they last longer than `limit` days. */
export const growthRunwayDays = (balance: number, base: number, growth: GrowthSettings, fromDay: number, limit = 1500): number | null => {
    let left = balance;
    for (let d = 1; d <= limit; d += 1) {
        left -= growthValueAt(base, growth, fromDay + d);
        if (left < 0) return d;
    }
    return null;
};

export const growthProblem = (settings: Pick<ProjectSettings, 'scheduleTrafficAmount' | 'growth'>): string | null => {
    const growth = settings.growth;
    if (!growth?.enabled) return null;
    const base = settings.scheduleTrafficAmount || 0;
    if (!GROWTH_PERCENTS.includes(growth.percent)) return 'Pick how much the daily visits grow.';
    if (!GROWTH_EVERY_DAYS[growth.every]) return 'Pick how often the daily visits grow.';
    if (!(growth.cap > base)) return 'Set the daily maximum above today’s visits per day.';
    return null;
};

/**
 * Before saving: a new base, percentage or interval starts the growth again from the base; a lower
 * cap only clamps what is delivered today. The nightly job does the rest.
 */
export const prepareGrowthForSave = (saved: ProjectSettings | undefined, next: ProjectSettings, today: string): ProjectSettings => {
    const growth = next.growth;
    if (!growth?.enabled) return next.growth ? { ...next, growth: { ...growth, enabled: false } } : next;
    const base = next.scheduleTrafficAmount || 0;
    const before = saved?.growth;
    const restart = !before?.enabled
        || (saved?.scheduleTrafficAmount || 0) !== base
        || before.percent !== growth.percent
        || before.every !== growth.every
        || !before.startedAt;
    if (restart) {
        return { ...next, growth: { ...growth, base, startedAt: today, activeDays: 0, current: base } };
    }
    return { ...next, growth: { ...growth, current: Math.min(growth.current ?? base, Math.max(growth.cap, base)) } };
};

/** The daily limit a save sends: today's grown value while growth is on, otherwise the base. */
export const dailyLimitFor = (settings?: Pick<ProjectSettings, 'scheduleTrafficAmount' | 'growth'>): number => {
    if (settings?.growth?.enabled && settings.growth.current) return settings.growth.current;
    return settings?.scheduleTrafficAmount || 0;
};
